import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { NotificationComponent } from '_app/components';
import { fetchProfileThunk } from '_app/features/session';
import Utils from '_app/utils';

import * as propertyApi from './propertyApi';
import { propertyQueryKeys } from './propertyQueryKeys';

export const usePropertyInteractions = ({ user, onPreApprovalSuccess = () => {} }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [tourSuccessKey, setTourSuccessKey] = useState(0);
  const [moreInfoSuccessKey, setMoreInfoSuccessKey] = useState(0);

  const invalidatePropertyCollections = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.collections });
    await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.tabCounts });
  }, [queryClient]);

  const invalidatePropertyDetail = useCallback(
    async (mlsnum) => {
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.detail(mlsnum) });
      await invalidatePropertyCollections();
    },
    [invalidatePropertyCollections, queryClient],
  );

  const ratingMutation = useMutation({
    mutationFn: propertyApi.rateProperty,
    onSuccess: async (_, variables) => {
      if (variables.status === 1 || variables.status === -1) {
        Utils.showInfoToast(variables.notification);
      }

      await invalidatePropertyDetail(variables.mlsnum);
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const sendChatMutation = useMutation({
    mutationFn: propertyApi.sendChat,
    onMutate: async ({ message, mlsnum = null }) => {
      const queryKey = propertyQueryKeys.chat(mlsnum);
      const previousMessages = queryClient.getQueryData(queryKey) || [];
      const optimisticMessage = {
        message,
        datetime: Date.now(),
        sender: { name: user?.name || 'Anonymous', is_agent: false },
      };

      queryClient.setQueryData(queryKey, [...previousMessages, optimisticMessage]);

      return { previousMessages, queryKey };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(context?.queryKey || propertyQueryKeys.chat(variables?.mlsnum), context?.previousMessages || []);
      Utils.showErrorToast(Utils.getError(error).message);
    },
    onSuccess: async (data, variables) => {
      if (!data.success) {
        Utils.showErrorToast(data.message || 'Sorry, something went wrong');
      }

      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.chat(variables.mlsnum) });
    },
  });

  const requestTourMutation = useMutation({
    mutationFn: propertyApi.requestPropertyTour,
    onSuccess: async (_, variables) => {
      setTourSuccessKey((value) => value + 1);
      dispatch(fetchProfileThunk());
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.chat(variables.mlsnum) });
      Utils.showInfoToast('Successfully contacted owner.');
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const requestMoreInfoMutation = useMutation({
    mutationFn: propertyApi.requestPropertyMoreInfo,
    onSuccess: async (_, variables) => {
      setMoreInfoSuccessKey((value) => value + 1);
      dispatch(fetchProfileThunk());
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.chat(variables.mlsnum) });
      Utils.showInfoToast('Successfully contacted owner.');
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const preApprovalMutation = useMutation({
    mutationFn: propertyApi.requestPropertyFinancing,
    onSuccess: async (data, mlsnum) => {
      if (!data.success) {
        Utils.showErrorToast(data.message || 'Sorry, something went wrong');
        return;
      }

      onPreApprovalSuccess(mlsnum);
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.chat(mlsnum) });
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const createRatingNotification = useCallback(
    (item, nextStatus, text) => {
      let notification;

      notification = (
        <NotificationComponent
          text={text}
          undoAction={() => {
            ratingMutation.mutate({
              mlsnum: item.mlsnum,
              status: item.rating,
              notification,
            });
          }}
        />
      );

      ratingMutation.mutate({
        mlsnum: item.mlsnum,
        status: nextStatus,
        notification,
      });
    },
    [ratingMutation],
  );

  const onLike = useCallback(
    (item) => {
      createRatingNotification(item, item.rating === 1 ? 0 : 1, `${item.address} added to Favs. `);
    },
    [createRatingNotification],
  );

  const onDisLike = useCallback(
    (item) => {
      createRatingNotification(item, item.rating === -1 ? 0 : -1, `${item.address} added to Reject. `);
    },
    [createRatingNotification],
  );

  return {
    invalidatePropertyCollections,
    invalidatePropertyDetail,
    chatInteractions: {
      loading: sendChatMutation.isLoading,
      onSend: (message, mlsnum = null) => sendChatMutation.mutate({ message, mlsnum }),
    },
    propertyActions: {
      user,
      onLike,
      onDisLike,
      onRequestTour: (payload) => requestTourMutation.mutate(payload),
      onRequestMoreInfo: (payload) => requestMoreInfoMutation.mutate(payload),
      onPreApproval: (mlsnum) => preApprovalMutation.mutate(mlsnum),
      tourRequestState: {
        loading: requestTourMutation.isLoading,
        successKey: tourSuccessKey,
      },
      moreInfoRequestState: {
        loading: requestMoreInfoMutation.isLoading,
        successKey: moreInfoSuccessKey,
      },
    },
  };
};
